import type { MultiValue, Props as SelectProps, SingleValue } from "react-select";
import { SelectInput } from "components/Select";
import {
  useGetRegionalResponsibleQuery,
  useGetStoresOptionsQuery,
  useGetStoresResponsiblesOptionsQuery,
} from "@/services/store/select-options";
import { RoleEnum } from "@/enum/role";
import { filterClasses, getSelectClasses } from "@/utils/filter-styles";
import { MultiValueTag } from "@/components/ui/MultiValueTag";
import { formatOptionLabel } from "@/utils/format-option-label";
import { useDepositStoresFilterActions } from "../../ducks/filter/hooks/actions";
import { useDepositStoresFilterState } from "../../ducks/filter/hooks/filter-state";
import { DEPOSIT_DIVERGENCE_RANGES } from "../../constants/divergence-ranges";
import { STATUS_OPTIONS } from "../../constants/status-options";

type OptionType = { value: string; label: string };

const FilterSelect = SelectInput as (
  props: SelectProps<OptionType, boolean>,
) => JSX.Element;

function isMultiSelection(
  option: MultiValue<OptionType> | SingleValue<OptionType>,
): option is MultiValue<OptionType> {
  return Array.isArray(option);
}

export default function SelectFilters() {
  const { updateFilter } = useDepositStoresFilterActions();
  const { filters } = useDepositStoresFilterState();

  const { data: storeOptions } = useGetStoresOptionsQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });
  const { data: responsibleOptions } = useGetStoresResponsiblesOptionsQuery(
    { role: RoleEnum.ANALISTA },
    { refetchOnMountOrArgChange: false },
  );
  const { data: regionOptions } = useGetRegionalResponsibleQuery(undefined, {
    refetchOnMountOrArgChange: false,
  });

  const handleChange =
    (key: string) =>
    (option: MultiValue<OptionType> | SingleValue<OptionType>) => {
      let value: string | string[] | undefined;
      if (isMultiSelection(option)) {
        value = option.length > 0 ? option.map(opt => opt.value) : undefined;
      } else {
        value = option?.value ?? undefined;
      }
      updateFilter({ key, value });
      updateFilter({ key: "meta", value: { page: 1 } });
    };

  const getMultiSelected = (key: string, options: Array<OptionType>) => {
    const v = filters[key];
    if (!v) return [];
    if (Array.isArray(v)) return options.filter(o => v.includes(o.value));
    return options.filter(o => o.value === v);
  };

  const getSingleSelected = (key: string, options: Array<OptionType>) => {
    const v = filters[key];
    if (!v) return null;
    return options.find((o: OptionType) => o.value === v) ?? null;
  };

  const filtersData = [
    {
      key: "storeCodes",
      options: storeOptions || [],
      placeholder: "Selecionar loja",
      value: getMultiSelected("storeCodes", storeOptions || []),
      onChange: handleChange("storeCodes"),
    },
    {
      key: "status",
      options: STATUS_OPTIONS,
      placeholder: "Selecionar situação",
      value: getSingleSelected("status", STATUS_OPTIONS),
      onChange: handleChange("status"),
      isMulti: false,
    },
    {
      key: "regionalId",
      options: regionOptions || [],
      placeholder: "Selecionar regional",
      value: getSingleSelected("regionalId", regionOptions || []),
      onChange: handleChange("regionalId"),
      isMulti: false,
    },
    {
      key: "responsibleUserId",
      options: responsibleOptions || [],
      placeholder: "Selecionar responsável",
      value: getSingleSelected("responsibleUserId", responsibleOptions || []),
      onChange: handleChange("responsibleUserId"),
      isMulti: false,
    },
    {
      key: "divergenceRange",
      options: DEPOSIT_DIVERGENCE_RANGES,
      placeholder: "Selecionar faixa de divergência",
      value: getSingleSelected("divergenceRange", DEPOSIT_DIVERGENCE_RANGES),
      onChange: handleChange("divergenceRange"),
      isMulti: false,
    },
  ];

  return (
    <div className={filterClasses.container}>
      {filtersData.map(filter => {
        const isMulti = filter.isMulti !== false;
        const hasValue = isMulti
          ? Array.isArray(filter.value) && filter.value.length > 0
          : filter.value !== null && filter.value !== undefined;

        return (
          <div key={filter.key} className={filterClasses.filterItem}>
            <FilterSelect
              options={filter.options}
              placeholder={filter.placeholder}
              value={filter.value}
              onChange={filter.onChange}
              className={getSelectClasses(hasValue)}
              classNamePrefix="an-simple-select"
              isMulti={isMulti}
              isClearable
              closeMenuOnSelect={!isMulti}
              hideSelectedOptions={false}
              components={isMulti ? { MultiValue: MultiValueTag } : {}}
              formatOptionLabel={formatOptionLabel}
            />
          </div>
        );
      })}
    </div>
  );
}
